// Screen flow: start (login / guest) → career → briefing → workstation → debrief.
import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, apiState, type User } from './api';
import { loadAirport } from '../data/airports';
import type { Airport } from '../sim/airport';
import { GameSession } from '../game/session';
import { levelById, type LevelDef } from '../career/levels';
import { applyResult, emptyCareer, fromServerProgress, loadLocal, mergeCareers, saveLocal, toServerProgress, type Career, type ResultApplied } from '../career/progress';
import type { ScoreSummary, SimEvent } from '../sim/scoring';
import { StartScreen, CareerScreen, BriefingScreen, DebriefScreen, LeaderboardScreen, SettingsModal } from './screens';
import { Workstation } from './workstation';
import { Radio } from '../audio/radio';

type Screen =
  | { k: 'boot' }
  | { k: 'start' }
  | { k: 'career' }
  | { k: 'briefing'; level: LevelDef; session: GameSession }
  | { k: 'game'; level: LevelDef; session: GameSession }
  | { k: 'debrief'; level: LevelDef; summary: ScoreSummary; events: SimEvent[]; result: ResultApplied; saveError: string | null; seed: number }
  | { k: 'board' };

export function App() {
  const [screen, setScreen] = useState<Screen>({ k: 'boot' });
  const [user, setUser] = useState<User | null>(null);
  const [career, setCareer] = useState<Career>(() => loadLocal());
  const [settings, setSettings] = useState(false);
  const [airport, setAirport] = useState<Airport | null>(null);

  useEffect(() => {
    void loadAirport('EDDW').then(setAirport);
    // development shortcut: ?play=<level id> opens the shift directly
    const devLevel = import.meta.env.DEV ? new URLSearchParams(location.search).get('play') : null;
    const lv = devLevel ? levelById(devLevel) : undefined;
    if (lv) {
      void loadAirport(lv.airport).then(ap => { const s = new GameSession(lv, ap, Number(new URLSearchParams(location.search).get('seed') ?? 7)); (window as unknown as { __session: GameSession }).__session = s; setScreen({ k: 'game', level: lv, session: s }); });
      return;
    }
    api.me().then(r => { onLogin(r.user); }).catch(() => setScreen({ k: 'start' }));
    const unlock = () => Radio.unlock();
    window.addEventListener('pointerdown', unlock);
    return () => window.removeEventListener('pointerdown', unlock);
  }, []);

  const onLogin = useCallback((u: User) => {
    setUser(u);
    const server = fromServerProgress(u.progress);
    const merged = mergeCareers(loadLocal(), server ?? emptyCareer());
    setCareer(merged);
    saveLocal(merged);
    setScreen({ k: 'career' });
  }, []);

  const openBriefing = async (level: LevelDef, seed = Math.floor(Math.random() * 1e9)) => {
    const ap = await loadAirport(level.airport);
    const session = new GameSession(level, ap, seed);
    setScreen({ k: 'briefing', level, session });
  };

  const finish = async (level: LevelDef, session: GameSession) => {
    session.dispose();
    Radio.stopAll();
    const summary = session.world.score.summary();
    const result = applyResult(career, level, summary);
    setCareer(result.career);
    saveLocal(result.career);
    let saveError: string | null = null;
    if (user) {
      try { const r = await api.saveProgress(toServerProgress(result.career, user.progress)); setUser(r.user); }
      catch (e) { saveError = e instanceof ApiError && e.status === 401 ? 'Deine Sitzung ist abgelaufen – der Fortschritt wurde lokal gespeichert. Melde dich erneut an, um ihn zu übertragen.' : `Speichern auf dem Server fehlgeschlagen (${(e as Error).message}) – lokal gesichert.`; }
    }
    setScreen({ k: 'debrief', level, summary, events: [...session.world.events], result, saveError, seed: session.world.options.seed });
  };

  const logout = async () => { try { await api.logout(); } catch { /* ignore */ } setUser(null); setScreen({ k: 'start' }); };

  let body: JSX.Element;
  switch (screen.k) {
    case 'boot': body = <div className="page muted" style={{ paddingTop: 80 }}>FREQUENZ wird geladen …</div>; break;
    case 'start': body = <StartScreen airport={airport} apiOnline={apiState() !== 'offline'} onLogin={onLogin} onGuest={() => setScreen({ k: 'career' })} />; break;
    case 'career': body = <CareerScreen user={user} career={career} onPlay={l => void openBriefing(l)} onSettings={() => setSettings(true)} onBoard={() => setScreen({ k: 'board' })} onLogout={logout} onLogin={() => setScreen({ k: 'start' })} />; break;
    case 'briefing': body = <BriefingScreen level={screen.level} session={screen.session} onBack={() => setScreen({ k: 'career' })} onStart={() => { Radio.unlock(); setScreen({ k: 'game', level: screen.level, session: screen.session }); }} />; break;
    case 'game': body = <Workstation session={screen.session} onSettings={() => setSettings(true)} onEnd={() => void finish(screen.level, screen.session)} />; break;
    case 'debrief': body = <DebriefScreen {...screen} career={career} onCareer={() => setScreen({ k: 'career' })} onRetry={() => void openBriefing(screen.level)} onNext={id => { const l = levelById(id); if (l) void openBriefing(l); }} />; break;
    case 'board': body = <LeaderboardScreen user={user} onBack={() => setScreen({ k: 'career' })} />; break;
  }
  return <>{body}{settings && <SettingsModal onClose={() => setSettings(false)} />}</>;
}
